/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.0.0
 */

export type IFixed = string | boolean | undefined;

/**
 * @description Column property
 * @interface {IFixedColumn}
 *
 * @property {string | boolean} fixed - set column fixed position
 */
export interface IFixedColumn {
    accessor?: string;
    className?: string;
    columns?: IFixedColumns;
    fixed?: IFixed;
    id?: string;
    Header?: any;
    headerClassName?: string;
    minWidth?: number;
    show?: boolean;
    width?: number | string;
    canResize?: boolean,
    isResizing?: boolean,
}

/**
 * @description Type of columns property
 * @type {IFixedColumns}
 */
export type IFixedColumns = IFixedColumn[];

/**
 * @description The method returns column id or column accessor
 * @param {IFixedColumn} column column in table
 * @returns {string | null}
 */
export const getColumnId = (column: IFixedColumn): string | null => {
    if (column.id) {
        return column.id;
    }
    if (typeof column.accessor === 'string') {
        return column.accessor;
    }
    return null;
};

/**
 * @description The method checks that the column is fixed left
 * @param {IFixedColumn} column column in table
 * @returns {boolean}
 */
export const isLeftFixed = (column: IFixedColumn): boolean => {
    return column.fixed === true || column.fixed === 'left';
};
/**
 * @description The method checks that the column is fixed right
 * @param {IFixedColumn} column column in table
 * @returns {boolean}
 */
export const isRightFixed = (column: IFixedColumn): boolean => {
    return column.fixed === 'right';
};
/**
 * @description The method checks that the column is not fixed
 * @param {IFixedColumn} column column in table
 * @returns {boolean}
 */
export const isNotFixed = (column: IFixedColumn): boolean => {
    return !column.fixed;
};
/**
 * @description The method of dividing columns into three arrays: fixedLeft, fixedRight, notFixed
 * @param {IFixedColumns} columns list of columns in table
 * @returns {IFixedColumns}
 */
export const sortColumns = (columns: IFixedColumns): IFixedColumns => {
    return [
        ...columns.filter(isLeftFixed),
        ...columns.filter(isNotFixed),
        ...columns.filter(isRightFixed),
    ];
};

/**
 * @description The method checks "position:sticky" support
 * @returns {boolean}
 */
export const enableStickyPosition = () => {
    if (typeof window === 'undefined') { // document is undefined in SSR
        return true;
    }
    const el: HTMLElement = document.createElement('a');
    const mStyle = el.style;
    mStyle.cssText = 'position:sticky;position:-webkit-sticky;position:-ms-sticky;';
    return !!mStyle.position && mStyle.position.indexOf('sticky') !== -1;
};

/**
 * @description The method checks error
 * @param {IFixedColumns} columns list of columns in table
 */
export const checkErrors = (columns: IFixedColumns): void => {
    const hasGroups = !!columns.find((column: IFixedColumn) => column.columns);
    const fixedColumnsWithoutGroup = columns
        .filter((column: IFixedColumn) => column.fixed && !column.columns)
        .map(({ Header }) => `'${Header}'`);

    if (hasGroups && fixedColumnsWithoutGroup.length) {
        throw new Error(`WARNING react-table-hoc-fixed-column:
        \nYour ReactTable has group and fixed columns outside groups, and that will break UI.
        \nYou must place ${fixedColumnsWithoutGroup.join(' and ')}
        columns into a group (even a group with an empty Header label)\n`);
    }

    const bugWithUnderColumnsFixed: IFixedColumn | undefined = columns
        .find((parentCol: IFixedColumn) => {
            return !parentCol.fixed
                && parentCol.columns
                && parentCol.columns.find((col: IFixedColumn) => col.fixed);
        });

    if (bugWithUnderColumnsFixed && bugWithUnderColumnsFixed.columns) {
        const childBugs = bugWithUnderColumnsFixed.columns.find(({ fixed }) => fixed);
        throw new Error(`WARNING react-table-hoc-fixed-column:
        \nYour ReactTable contain columns group with at least one child columns fixed.
        \nWhen ReactTable has columns groups, only columns groups can be fixed
        \nYou must set fixed: 'left' | 'right' for the '${bugWithUnderColumnsFixed.Header}' column,
        or remove the fixed property of '${childBugs && childBugs.Header ? childBugs.Header : ''}' column.`);
    }
};

/**
 * @description The method search next not hidden column
 * @param {IFixedColumns} columns list of columns in table
 * @param {number} currentIndex current column index
 * @returns {IFixedColumn | undefined}
 */
export const findNextColumnNotHidden = (columns: IFixedColumns, currentIndex: number): IFixedColumn | undefined => {
    for (let i = currentIndex + 1; i < columns.length; i += 1) {
        const column = columns[i];
        if (column.show !== false) {
            return column;
        }
    }

    return undefined;
};

/**
 * @description The method search previous not hidden column
 * @param {IFixedColumns} columns list of columns in table
 * @param {number} currentIndex current column index
 * @returns {IFixedColumn | undefined}
 */
export const findPrevColumnNotHidden = (columns: IFixedColumns, currentIndex: number): IFixedColumn | undefined => {
    for (let i = currentIndex - 1; i >= 0; i -= 1) {
        const column = columns[i];
        if (column.show !== false) {
            return column;
        }
    }

    return undefined;
};
